import type { LocationScope } from "@order-up/models";
import {
  locationCollectionPath,
  locationSubdocPath,
  locationsCollectionId,
  tenantLocationsPath,
} from "../scope/locationScope";

// Collection names (industry naming: snake_case, plural for collections)
export const COLLECTIONS = {
  /** Platform admin accounts (OrderUp team only, separate from tenant users) */
  ADMINS: "admins",
  /**
   * Runtime-editable permission floors for non-founder SiteRoles
   * (`support`, `viewer`). One document per SiteRole, keyed by the
   * SiteRole string. `site_admin` is intentionally absent — its floor
   * (`['*']`) is the bootstrap identity and locked at code level.
   */
  SITE_ROLE_FLOORS: "site_role_floors",
  USERS: "users",
  /** Assignment lifecycle audit log (subcollection of users) */
  ASSIGNMENT_HISTORY: "assignment_history",
  /**
   * Second factors OrderUp owns (subcollection of users). SERVER-WRITE-ONLY:
   * a client able to write here can grant itself a factor it does not hold.
   * Holds AES-GCM ciphertext for TOTP secrets and phone numbers.
   */
  MFA_FACTORS: "mfa_factors",
  /**
   * Live step-up markers (subcollection of users) — "this SESSION already
   * passed a second factor". Server-write-only, bound to uid AND sessionId so
   * a concurrent session cannot spend a step-up it never performed.
   */
  MFA_STEP_UPS: "mfa_step_ups",
  /**
   * Append-only second-factor audit trail (subcollection of users).
   * Server-write-only. Records WHICH factor and WHETHER it succeeded — never a
   * secret, a submitted code, or an assertion payload.
   */
  MFA_AUDIT: "mfa_audit",
  /**
   * DSAR-ready snapshots written immediately before a user is hard-
   * deleted. Each manifest captures `assignments[]`, `onboardingStatus`,
   * `limboStartedAt`, `deletedByUid`, and the audit-event id at the
   * moment of deletion so a later GDPR / SOC 2 inquiry can reconstruct
   * what was erased without restoring backups.
   */
  DELETION_MANIFESTS: "deletion_manifests",
  /**
   * Workstream E — uniqueness index for verified email addresses across all
   * accounts. Each document is keyed by the lowercased email address and
   * contains `{ uid: string }`. Maintained atomically alongside the user's
   * `verifiedEmails[]` set so that one email can be verified by at most one
   * account at a time. Admin SDK only; rules will deny all client reads.
   */
  VERIFIED_EMAIL_INDEX: "verified_email_index",
  /** Platform-wide system roles (managed in admin app, read by tenants via client SDK) */
  SYSTEM_ROLES: "system_roles",
  /** Platform-wide allergy categories (managed in admin app, read-only for tenants) */
  ALLERGY_CATEGORIES: "allergy_categories",
  /** Platform-wide ingredients (managed in admin app, read-only for tenants) */
  INGREDIENTS: "ingredients",
  /** Platform-wide business types (managed in admin app, read-only for tenants) */
  BUSINESS_TYPES: "business_types",
  /** Platform-wide business sub-categories (managed in admin app, read-only for tenants) */
  BUSINESS_SUB_CATEGORIES: "business_sub_categories",
  /**
   * Cross-tenant business relationships (Phase 1 "Accountant Access"). Each
   * doc is a consented request→confirm agreement that authorizes granting one
   * business the restaurant `accountant` role on another tenant's restaurant.
   * Top-level because a connection spans two distinct tenants.
   */
  BUSINESS_CONNECTIONS: "business_connections",
  TENANTS: "tenants",
  RESTAURANTS: "restaurants", // scoped-path-ok: collection name declaration
  LOCATIONS: "locations", // scoped-path-ok: collection name declaration
  /**
   * Marketplace-facing brands operating out of one restaurant (subcollection of
   * restaurants). Doc id IS the brand's `guid` — the value written to KitchenHub
   * as `partner_store_id`, which is the ONLY key an inbound aggregator order
   * carries to say which brand, and therefore which restaurant, it belongs to.
   *
   * Every restaurant has exactly one `isDefault` brand, so a single-brand
   * restaurant and a five-brand ghost kitchen run the identical code path.
   * See packages/models/src/brands/Brand.ts.
   */
  BRANDS: "brands",
  PRINTERS: "printers",
  PAYMENT_TERMINALS: "payment_terminals",
  PREP_STATIONS: "prep_stations",
  ITEMS: "items",
  COURSES: "courses",
  SALES_CATEGORIES: "sales_categories",
  DISCOUNTS: "discounts",
  TAX_RATES: "tax_rates",
  MODIFIER_GROUPS: "modifier_groups",
  MODIFIERS: "modifiers",
  // RecipeOps (Phase 14-R) — operational BOM + stock (manager-only read)
  RECIPE_ITEMS: "recipe_items",
  /**
   * A restaurant's bindings to the platform bar catalog: which menu group
   * holds which adopted collection, plus the per-entry sync baseline that
   * makes a local edit distinguishable from an untouched field.
   * Server-write-only — see firestore-rules/20-bar-catalog.rules.
   */
  BAR_ADOPTIONS: "bar_adoptions",
  BAR_OPEN_BOTTLES: "bar_open_bottles",
  BAR_OPEN_BOTTLE_POURS: "bar_open_bottle_pours",
  INVENTORY_ITEMS: "inventory_items",
  MODIFIER_INGREDIENTS: "modifier_ingredients",
  // Server-only order-close idempotency markers (admin-SDK only; rules deny all)
  RECIPEOPS_DECREMENTS: "recipeops_decrements",
  RECIPEOPS_RESTOCKS: "recipeops_restocks",
  /**
   * Server-only once-per-order marker for the menu-level stock decrement at
   * FIRE (spec B5). Doc id IS the order guid (a deterministic key — the fire
   * path is at-least-once: activator re-ticks, webhook redeliveries). Written
   * in the same transaction as the decrement; rules deny all clients.
   */
  STOCK_DECREMENTS: "stock_decrements",
  /**
   * Server-only low-stock alert outbox (bar v2 design 2026-09-30 §C3). One doc
   * per crossing at the deterministic id `{inventoryGuid}:{crossedAtEpochMs}`,
   * CREATED in the transaction that took the item below its level — so a
   * crossing and its alert commit together — then delivered as a
   * `notifications` doc by `LowStockAlertService`. Rules deny all clients.
   */
  LOW_STOCK_ALERTS: "low_stock_alerts",
  /**
   * Paper supplies (design 2026-10-26 §A) — ONE doc per location at
   * `paper_supply/current`: rolls on hand per paper width, the per-day usage
   * the tally measured from `print_records`, the low-paper edge, and the
   * tally's cursor. Server-only; rules deny all clients (the API reads it).
   */
  PAPER_SUPPLY: "paper_supply",
  /**
   * Paper supply history, APPEND-ONLY: counts, receipts, settings changes and
   * delivered orders, one doc each. A delivered supply order's credit lives at
   * the deterministic id `order-{orderId}` (created once, in the transaction
   * that credits the stock). Server-only.
   */
  PAPER_SUPPLY_EVENTS: "paper_supply_events",
  /**
   * Low-supply alerts and the reorder offer they carry (design 2026-10-26),
   * one doc per crossing at `paper-{width}-{crossedAtEpochMs}`, CREATED in the
   * transaction that took the width below its level, then delivered to the
   * location's managers as `notifications` docs. Server-only.
   */
  SUPPLY_OFFERS: "supply_offers",
  /**
   * Books posting state of a physical bar count (bar v2 design 2026-09-30
   * §C2), one doc per APPLIED CHUNK at `{countGuid}_{chunkIndex}`: pending →
   * posted / needs_cost / failed, with the retry clock. The count itself
   * (`inventory_counts`) is immutable, so the one thing that changes after a
   * count lives here instead. Server-only; rules deny all clients.
   */
  INVENTORY_COUNT_POSTINGS: "inventory_count_postings",
  /**
   * A physical bar count's LINES (bar v2 §C2): `inventory_counts/{countGuid}/
   * inventory_count_lines/{inventoryGuid}`, one per counted item. Named for
   * its parent, never a bare `lines`: the mirror classifies by name, and a
   * generic name would silently classify every future `lines` subcollection. APPEND-ONLY with its header —
   * created once, never updated or deleted (`audit-append-only` guard).
   * Server-only; rules deny all clients.
   */
  INVENTORY_COUNT_LINES: "inventory_count_lines",
  /**
   * A physical bar count's TOTALS (bar v2 §C2): `inventory_counts/{countGuid}/
   * inventory_count_summary/{CANONICAL_DOC_IDS.INVENTORY_COUNT_SUMMARY_TOTALS}`,
   * written once when the count completes. Named for its parent, like the lines. APPEND-ONLY with its header. Server-only.
   */
  INVENTORY_COUNT_SUMMARY: "inventory_count_summary",
  /**
   * Cash drawer ENTITY documents — one doc per physical drawer.
   * Doc id IS the drawer's `guid` (`ICashDrawer.guid`), which is what
   * `cash_drawer_sessions.drawerGuid` and `cash_drawer_events.drawerGuid` point
   * at. Before this collection existed a drawer was an array element on
   * `printers/{id}.cashDrawers[]`, so both of those references pointed at NO
   * document: deleting an array element dangled the history silently, and
   * resolving a drawer meant loading every printer and scanning arrays.
   *
   * NOTE — this name previously held the EVENT log. That ledger now lives in
   * {@link CASH_DRAWER_EVENTS}. Anything still reading `cash_drawers` expecting
   * movements is reading drawers.
   */
  CASH_DRAWERS: "cash_drawers",
  /**
   * Cash-management EVENT log — ONE doc per cash movement (deposit, pay-out,
   * float, variance, no-sale). Doc id is the client-minted `eventId`, which is
   * also the Books RPC's idempotency key. The Books trigger
   * `onCashDrawerStatusChanged` watches this collection.
   *
   * Renamed out of `cash_drawers`: a collection named for an entity that holds
   * that entity's events cannot be reasoned about, and it made a real drawer
   * document impossible to store.
   */
  CASH_DRAWER_EVENTS: "cash_drawer_events",
  // Card holds (pre-auth card on file) for tabs and table service, one document
  // per hold under the location (docs/CARD_HOLDS.md). SERVER-ONLY both ways in
  // firestore.rules: a document carries the TRX StorageSafe token the hold is
  // re-authorized on. Never a payments record; the order carries a token-free
  // `cardHold` projection. Dark behind `initiative.card_holds`.
  CARD_HOLDS: "card_holds",
  // Cash-drawer SHIFT sessions (CD-7) — the ledger of record for an open till.
  // The POS CashDrawerCubit is a cache over these docs, not the other way round.
  CASH_DRAWER_SESSIONS: "cash_drawer_sessions",
  // A counted close's PLAN (A8b/A8d) — one document per session, nested under
  // it at CANONICAL_DOC_IDS.CASH_DRAWER_CLOSE_PLAN_PRIMARY. Server-only both
  // ways in firestore.rules: it carries the live EXPECTED total of a till that
  // is still open, and the session document above is member-readable. Declared
  // here, not built inside the service, so everything that walks collections
  // (the prod→dev mirror's MIRROR_POLICY, which REFUSES an unclassified name
  // and aborts the run) can see it.
  CASH_DRAWER_CLOSE_PLAN: "close_plan",
  // OrderUp billing standing — the failed-payment grace period / POS
  // suspension mirror, one document (`current`) under each location. Written by
  // the server's grace service only; read by that location's staff and POS.
  // Its own sub-document because the location document is publicly readable.
  LOCATION_BILLING_STANDING: "billing_standing",
  KITCHEN_BUZZERS: "kitchen_buzzers",
  MENU_GROUPS: "menu_groups",
  // Digital menu boards (signage) — one document per WALL, not per panel; a
  // board describes every screen in the store and each appliance narrows to the
  // panels it drives (docs/MENU_BOARD_ARCHITECTURE.md §3/§5).
  //
  // Doc ID = the board's own `boardId`. That is deliberate rather than an auto
  // id + a `boardId` field: §6 addresses the poll as `menu_boards/{boardId}`, so
  // an auto id would mean the gateway had to QUERY for a document it can name.
  // The id is server-minted (the caller never supplies one) and the board
  // document repeats it, so a raw Firestore dump still identifies itself.
  MENU_BOARDS: "menu_boards",
  // Immutable published snapshots of ONE board, nested under that board's
  // document. The board document is the working copy an owner edits; a
  // document here is a version that was actually published and is never
  // written again (docs/MENU_BOARD_ARCHITECTURE.md §6).
  //
  // The split is not tidiness. When a draft and the published board shared one
  // document, the first Save after a publish OVERWROTE the content hanging on
  // the wall: `status` flipped to 'draft', so every fetch path that filters
  // `status === 'published'` matched nothing, and the bytes a live panel was
  // showing existed nowhere in Firestore. Same rule as NETWORK_CONFIGS — a
  // published version is immutable and a change is a new version — which is
  // also what gives a rollback something to roll back TO.
  //
  // Auto doc id, ordered by the `version` field (never by id): version 10 must
  // sort after version 9, and a lexicographic id would not.
  MENU_BOARD_VERSIONS: "board_versions",
  // OrderUp Studio designs — one document per artboard (flyer, poster, table
  // tent, social post). Doc ID = the design's own `designId`, mirroring
  // MENU_BOARDS: server-minted, repeated inside the document, so a raw export
  // still identifies itself and every read addresses a document it can name.
  //
  // Images are referenced, never inlined, so a design stays far under
  // Firestore's 1 MiB document cap however many photos it places.
  DESIGNS: "designs",
  // Immutable published snapshots of ONE design, nested under it.
  //
  // The split is copied from MENU_BOARD_VERSIONS deliberately, and for the same
  // reason: when a working copy and its published version shared one document,
  // the first Save after a publish destroyed what was live. Here the loss is
  // quieter but worse — an operator sends a flyer to a printer, keeps editing,
  // and the artwork that was actually approved no longer exists anywhere. A
  // published version is immutable; a change is a new version.
  //
  // Auto doc id, ordered by the `version` FIELD — a lexicographic id would sort
  // version 10 before version 9.
  DESIGN_VERSIONS: "design_versions",
  // Printed menus — a letter-size handout built from the live menu
  // (docs/PRINTED_MENU_ARCHITECTURE.md). Doc ID = the record's own
  // `printedMenuId`: server-minted, repeated inside the document, the same
  // shape as MENU_BOARDS and DESIGNS. The WORKING COPY — settings the owner
  // edits, plus a pointer to the newest saved version.
  PRINTED_MENUS: "printed_menus",
  // Saved versions of ONE printed menu, nested under it. Immutable: written once
  // by the transaction that allocates the version number, never updated, so a
  // handout that was printed or emailed can be reopened exactly as it was after
  // the menu has changed. Auto doc id, ordered by the `version` FIELD — a
  // lexicographic id would sort version 10 before version 9.
  PRINTED_MENU_VERSIONS: "printed_menu_versions",
  // One document per email send of a printed menu version (recipients, note,
  // sender, provider outcome). Append-only apart from the sending → sent/failed
  // status move. Auto doc id: one writer, and a second send is a second send.
  PRINTED_MENU_DELIVERIES: "printed_menu_deliveries",
  REVENUE_CENTERS: "revenue_centers",
  CATERING_LEADS: "catering_leads",
  CATERING_ORDERS: "catering_orders",
  CATERING_SETTINGS: "catering_settings",
  ADVERTISING_REQUESTS: "advertising_requests",
  TAGS: "tags",
  VOID_REASONS: "void_reasons",
  EMPLOYEES: "employees",
  // Manager-only subcollection under an employee doc holding sensitive
  // comp/PII (wage, SSN-last-4, DOB, W-4, bank accounts, …). Read-gated to
  // canWriteRestaurant() + self; Admin-SDK-only writes. See SOC2 #48/H-DP2.
  EMPLOYEE_PRIVATE: "private",
  // Append-only manager-approval handshake queue under an employee doc:
  // staff PROPOSES a direct-deposit change (Admin-SDK write only), it stays
  // `pending` until a manager APPROVES. Documents only ever hold MASKED display
  // fields plus the AES-encrypted account payload — never raw bank numbers.
  DIRECT_DEPOSIT_REQUESTS: "direct_deposit_requests",
  JOB_TITLES: "job_titles",
  // Staff onboarding document CONFIG (title/targeting/Drive refs). Server-only
  // collection (API Admin SDK) — signing state lives in Supabase OrderSign.
  ONBOARDING_DOCUMENTS: "onboarding_documents",
  DEPARTMENTS: "departments",
  LABOR_WARNINGS: "labor_warnings",
  SCHEDULES: "schedules",
  SHIFT_REQUESTS: "shift_requests",
  SHIFTS: "shifts",
  SHIFT_TASKS: "shift_tasks",
  SHIFT_TASK_COMPLETIONS: "shift_task_completions",
  TIME_ENTRIES: "time_entries",
  // Append-only clock-in-photo consent events (accept/decline per policy
  // version + language). Create-only at the rules layer — the consent HISTORY
  // is the evidentiary artifact, so no client may rewrite it.
  TIMECLOCK_CONSENTS: "timeclock_consents",
  /** A station's checkout for a business day, completed on the till by a staff PIN. */
  STATION_CHECKOUTS: "station_checkouts",
  // Staff app — per-restaurant team chat between employees/managers (legacy flat thread)
  TEAM_MESSAGES: "team_messages",
  // Industry-grade team chat (channels, DMs, threads). chat_v2.
  CHAT_CHANNELS: "chat_channels",
  CHAT_MEMBERS: "members", // subcollection of chat_channels/{channelId}
  CHAT_MESSAGES: "messages", // subcollection of chat_channels/{channelId}
  CHAT_MESSAGE_ACKS: "acks", // subcollection of .../messages/{messageId}
  // Membership index keyed by Firebase auth uid (linkedUserId) — lets
  // firestore.rules gate private-channel reads via exists() without mapping
  // uid→employeeGuid (impossible in rules for account logins). Maintained by
  // ChatService/ChatProvisioningService alongside members.
  CHAT_READER_UIDS: "reader_uids", // subcollection of chat_channels/{channelId}
  CHAT_USER_STATE: "chat_user_state", // per-employee unread roll-up
  // Management app — manager's log (shift-handoff entries, manager-authored)
  MANAGER_LOG_ENTRIES: "manager_log_entries",
  // Management app — pre-shift briefing notes surfaced to staff before a shift
  PRE_SHIFT_NOTES: "pre_shift_notes",
  PIN_LOOKUP: "pin_lookup",
  PIN_AUDIT_LOGS: "pin_audit_logs",
  /** Ops actions on one payment (subcollection of payments) — server-write-only, e.g. scripts/ops/release-auth-hold-orphan.ts. */
  PAYMENT_OPS_EVENTS: "payment_ops_events",
  /**
   * Brute-force cool-off state for PIN entry, one doc per caller. Server-write-
   * only (no rule matches it, so clients are denied): a client that could write
   * it could clear its own penalty. Written by Firebase Functions'
   * `verifyEmployeePin` (keyed on the POS deviceId) and by the API's
   * operator-assertion issuance (keyed on the authenticated principal).
   */
  PIN_THROTTLE: "pin_throttle",
  /**
   * One-use ledger for APPROVAL operator assertions, keyed by the assertion's
   * `jti` (top-level). Server-write-only; a Firestore TTL policy on `expireAt`
   * deletes each entry after its assertion has expired. See
   * apps/api/components/utils/operatorAssertionLedger.ts.
   */
  OPERATOR_ASSERTION_CONSUMPTIONS: "operator_assertion_consumptions",
  /**
   * Remote manager approval (docs/superpowers/specs/2026-10-16-remote-manager-approval-design.md).
   * All four are TOP-LEVEL and server-only — no rule matches them, so every
   * client is denied. Requests are keyed by the till-minted requestGuid and are
   * kept (approval evidence); links, the PIN throttle and the text counts expire
   * by a Firestore TTL policy on `expiresAt`.
   */
  MANAGER_APPROVAL_REQUESTS: "manager_approval_requests", // scoped-path-ok: not location-scoped (indexed by requestGuid; carries tenantDocId/locationDocId)
  /** One per texted approver, keyed by the SHA-256 of the link token — never the token. */
  MANAGER_APPROVAL_LINKS: "manager_approval_links", // scoped-path-ok: not location-scoped (keyed by the token hash; carries tenantDocId/locationDocId)
  /** Tenant-wide per-PERSON throttle for remote decisions, keyed by sha256(personKey). */
  MANAGER_APPROVAL_PIN_THROTTLE: "manager_approval_pin_throttle", // scoped-path-ok: not location-scoped (one budget per person across a tenant's locations)
  /** Approval texts per location per LOCAL day: `${tenantDocId}__${locationDocId}__${yyyy-mm-dd}`. */
  MANAGER_APPROVAL_TEXT_COUNTS: "manager_approval_text_counts", // scoped-path-ok: not location-scoped (keyed by tenant__location__day; carries tenantDocId/locationDocId)
  /**
   * Shift alerts and the on-time record (docs/superpowers/specs/2026-10-21-shift-alerts-and-attendance-design.md §10).
   * All four are SERVER-ONLY — rules deny every client read and write
   * (firestore-rules/24-shift-alerts.rules). The first three are location
   * subcollections (CollectionPaths.shiftAlerts / attendanceRecords /
   * shiftAlertSubscribers); the text counter is top-level.
   *
   * One doc per (shift, rung): the idempotency ledger of what was sent. TTL 30 days on `expireAt`.
   */
  SHIFT_ALERTS: "shift_alerts",
  /** One doc per tracked shift: the on-time record a manager reads and excuses. TTL 2 years on `expireAt`. */
  ATTENDANCE_RECORDS: "attendance_records",
  /** One doc per manager who receives the location's late and no-show alerts. */
  SHIFT_ALERT_SUBSCRIBERS: "shift_alert_subscribers",
  /**
   * An employee's request about their own data (a copy, or deletion), lodged
   * from the staff portal. Deterministic id: `{employeeGuid}__{kind}__{day}`.
   * Server-only.
   */
  EMPLOYEE_DATA_REQUESTS: "employee_data_requests",
  /** Shift-alert texts per location per LOCAL day: `${tenantDocId}__${locationDocId}__${yyyy-mm-dd}`. */
  SHIFT_ALERT_TEXT_COUNTS: "shift_alert_text_counts", // scoped-path-ok: not location-scoped (daily cap keyed by tenant__location__date)
  /**
   * An employee's consent to be texted about their shifts, one doc per (person,
   * phone number) at a location — the record SOC 2 R40 asks for
   * (packages/models/src/employees/employeeTextConsent.ts). SERVER-ONLY: rules
   * deny every client (firestore-rules/24-shift-alerts.rules); written with an
   * `audit_events` row in the same transaction. Id: `employeeTextConsentId`.
   */
  EMPLOYEE_TEXT_CONSENTS: "employee_text_consents",
  /**
   * What the deletion retry did about one FAILED guest-erasure request, keyed
   * by that request's id (`customer_data.deletion_requests.id`): the retry it
   * opened, or the alert it sent once the chain was exhausted. Server-only.
   */
  DELETION_REQUEST_RETRIES: "deletion_request_retries", // scoped-path-ok: not location-scoped (keyed by a Supabase deletion request id)
  COUNTERS: "counters",
  /**
   * What each separately-deployed half of the platform has told the other it
   * can do — `functions` (confirmed by a Functions call carrying the
   * capability header) and `api` (confirmed by an API answer). One doc per
   * half, `capabilities.{name}` = when it was last confirmed. Read with a
   * freshness window, so a rolled-back half stops being trusted by itself.
   * Server-write-only; any signed-in client may READ it (the till reads `api`
   * before sending a paid order's cancel to the server — names and
   * timestamps only). packages/core/src/services/platformCapabilities.ts.
   */
  PLATFORM_CAPABILITIES: "platform_capabilities",
  /**
   * A2P 10DLC opt-out ledger, keyed by E.164 phone number.
   *
   * TOP-LEVEL, not tenant-scoped, and deliberately so: every restaurant's SMS
   * leaves from ONE platform-level sender (`SenderID='OrderUp'` on SNS, a
   * single number on Twilio/Sent). A recipient replying STOP is opting out of
   * that sender — they have no way to express "only Tony's". Scoping the
   * suppression per-restaurant would let the next restaurant on the platform
   * text a number that already said stop, from the same number they said it to.
   */
  SMS_OPT_OUTS: "sms_opt_outs",
  SITE_CONFIGURATION: "site_configuration",
  // Reviews from multiple sources (Google Business, Yelp, etc.)
  REVIEWS: "reviews",
  // One doc per Google review OrderUp auto-replies to, keyed by the Google
  // review id and created with transaction.create (exactly one reply per
  // review). Server-written only; location staff may read.
  REVIEW_REPLIES: "review_replies",
  // Google listing sync: one singleton doc (id `state`) of per-field drift state
  // per location, and its change log (90-day TTL on `expireAt`). Server-written
  // only; location staff may read.
  GOOGLE_LISTING_SYNC: "google_listing_sync",
  GOOGLE_LISTING_CHANGES: "google_listing_changes",
  // Drafted Google Business Profile posts awaiting the owner's approval.
  // Server-written only (the API); no client rules grant access.
  GOOGLE_BUSINESS_POST_DRAFTS: "google_business_post_drafts",
  // Media (photos/videos) from multiple sources (Google Business, uploads, etc.)
  MEDIA: "media",
  /**
   * @deprecated Phase 11 — use `DOMAINS`. The legacy `email_identities`
   * collection is read-only via `firestore.rules`; writes are denied.
   * Removal target: 2026-07-26 (90 days after Phase 11 deprecation, 2026-04-26).
   * See `docs/RUNBOOKS/legacy-collection-deletion-schedule.md`.
   * (The sibling `DOMAIN_MAPPINGS` constant was removed 2026-07-09 —
   * its code stack had zero remaining callers.)
   */
  EMAIL_IDENTITIES: "email_identities",
  // Unified domain management (hosting, email, CDN) — replaces the legacy
  // `domain_mappings` collection (constant removed 2026-07-09) and EMAIL_IDENTITIES
  DOMAINS: "domains",
  // Third-party integrations (Google Business, Yelp, DoorDash, etc.)
  INTEGRATIONS: "integrations",
  /** Tier-2 AI content drafts awaiting a human publish (ORDERUP_AI_ARCHITECTURE §5). */
  AI_CONTENT_DRAFTS: "ai_content_drafts",
  // Top-level correlation index for async delivery-provider report jobs. Keyed
  // by the provider `workflow_id` returned at report-request time so the later
  // store-less report-ready webhook (Uber `eats.report.success` carries only a
  // `job_id`, no store/tenant id) can be routed back to its integration.
  DELIVERY_REPORT_JOBS: "delivery_report_jobs",
  // Customer carts for ordering applications
  CARTS: "carts",
  // Restaurant orders (single source of truth — terminal states stay here
  // with orderState=cancelled|voided, gated by security rule immutability)
  ORDERS: "orders",
  // Payment records (idempotency consolidated into payment records)
  PAYMENTS: "payments",
  // Settlement batch history (durable mirror of processor batches)
  BATCHES: "batches",
  // Order operations and idempotency (three-axis state)
  ORDER_OPERATIONS: "order_operations",
  ORDER_IDEMPOTENCY: "order_idempotency",
  KITCHEN_OPERATIONS: "kitchen_operations",
  KITCHEN_IDEMPOTENCY: "kitchen_idempotency",
  // Payment method configuration
  PAYMENT_METHODS: "payment_methods",
  // Live offline-capture queue status reported by the POS device.
  // Single doc per restaurant (`current`); last-writer-wins on
  // multi-device tenants. See `OfflineQueueReporterService`.
  OFFLINE_QUEUE_STATUS: "offline_queue_status",
  // Restaurant-scoped counters, one doc per purpose. Today only
  // `_meta/printer_config` — the monotonic printer-config version that both
  // printerConfigSyncService copies (packages/core and apps/functions)
  // increment atomically, and that a gateway uses to ignore a stale config.
  RESTAURANT_META: "_meta",
  // Table Service - Table Layouts
  TABLE_LAYOUTS: "table_layouts",
  // Table Service - Table Management
  TABLE_ASSIGNMENTS: "table_assignments",
  TABLE_MERGES: "table_merges",
  // Customer Management
  WAITLIST_ENTRIES: "waitlist_entries",
  SEATED_CUSTOMERS: "seated_customers",
  RESERVATIONS: "reservations",
  // Device Management (restaurant level)
  DEVICES: "devices",
  DEVICE_CONFIG: "device_config",
  // Device provisioning sessions (TOP-LEVEL, server-only). The relay that
  // carries a signed-in operator's ID token from the auth origin back to a
  // device that has no custom URL scheme to receive it — the gateway's
  // equivalent of the POS's `letsorderup://signin/complete` deep link.
  // Short-lived: a document exists only between the auth bounce and the
  // device's next poll, and is burned on read.
  DEVICE_PROVISIONING_SESSIONS: "device_provisioning_sessions",
  // Order counters (per tenant, per day)
  // Unified notification system (SSOT)
  NOTIFICATIONS: "notifications",
  // New-Order Awareness ack + escalation state (one doc per requireAck order,
  // doc id = orderGuid). Read-only to POS clients; API/admin-SDK writes only.
  ORDER_AWARENESS: "order_awareness",
  // User invitations (platform and tenant)
  INVITATIONS: "invitations",
  // Door B self-service workplace claim codes (manager-issued, person-redeemed)
  RESTAURANT_CLAIM_CODES: "restaurant_claim_codes",
  // Pending claim-code redemptions awaiting manager confirm (handshake)
  RESTAURANT_CLAIM_REDEMPTIONS: "restaurant_claim_redemptions",
  // Role-based permissions (per tenant, document id = roleId)
  ROLE_PERMISSIONS: "role_permissions",
  // Job-based permissions (per restaurant, document id = jobId as string)
  JOB_PERMISSIONS: "job_permissions",
  // OrderUp Books - Accounting and financial management
  // ALL Books domains have migrated to Supabase. The original Firestore
  // collections (BOOK_ACCOUNTS, BOOK_SETTINGS, BOOK_JOURNAL_ENTRIES,
  // BOOK_CUSTOMERS, BOOK_VENDORS, BOOK_INVOICES, BOOK_BILLS, BOOK_PAYMENTS,
  // BOOK_FISCAL_PERIODS, BOOK_RECONCILIATIONS, BOOK_AUDIT_LOGS) were removed
  // across Phases 1-7. Books data lives in Supabase Postgres tables of the
  // same names; the browser reaches them through the Fastify API only.
  // See docs/books-rebuild/BOOKS_REBUILD_PLAN.md Phases 1-7.
  // Customer profiles — per-restaurant relationship with a global Customer (W4)
  CUSTOMER_PROFILES: "customer_profiles",
  // Customer merge audit — append-only records tracking identity merges (W4)
  CUSTOMER_MERGES: "customer_merges",
  // Receipt claims — customer-initiated "claim a missed purchase" records
  // (per-restaurant; admin-SDK writes only, staff read via management queue)
  RECEIPT_CLAIMS: "receipt_claims",
  // Loyalty program configuration — one per restaurant (W4)
  LOYALTY_PROGRAMS: "loyalty_programs",
  // Loyalty event stream — append-only earn/burn/adjust/expire events (W4)
  LOYALTY_EVENTS: "loyalty_events",
  // Server-only per-order idempotency markers for lifetime spend/order-count
  // accrual on a customer profile (admin-SDK only; rules deny all). One doc
  // keyed by orderId — its presence proves `recordOrder` already ran for that
  // order, so a re-delivered order-completion is a no-op (no double-count).
  LOYALTY_ORDER_MARKERS: "loyalty_order_markers",
  // Server-only customer card-on-file refs, nested under a customer profile so
  // a saved card is naturally scoped to (tenant, restaurant, customer) — saved
  // tokens are provider- AND merchant-scoped. Caches the vault routing ref +
  // masked display; the provider vault is the system of record. Admin-SDK-only
  // writes (rules deny client writes). See models/customers/SavedPaymentMethod.
  SAVED_PAYMENT_METHODS: "saved_payment_methods",
  // Card-fingerprint index — POS-runtime identity lookup (Phase 14).
  // SHA-256(BIN + last4 + expiry_month) → customer link. PAN never stored.
  // PCI SAQ-A preserved. Server-write only via CardFingerprintService.
  CARD_FINGERPRINTS: "card_fingerprints",
  // Newsletter subscribers (per restaurant)
  NEWSLETTER_SUBSCRIBERS: "newsletter_subscribers",
  // Contact form submissions (per restaurant)
  CONTACT_SUBMISSIONS: "contact_submissions",
  // Contact form subject options (per restaurant)
  CONTACT_SUBJECTS: "contact_subjects",
  // Print records — audit log of all print jobs dispatched to the gateway
  PRINT_RECORDS: "print_records",
  /**
   * MERCHANT-VISIBLE record of every OrderUp support session that entered this
   * restaurant (subcollection of restaurants). SERVER-WRITE-ONLY.
   *
   * The `impersonation_sessions` collection already records these, but it is
   * TOP-LEVEL and site-admin-only: the merchant whose data was read cannot see
   * it. That is the wrong default for a vendor with a console that can open any
   * restaurant — accountability that only the vendor can audit is not
   * accountability. One document per session, written under the restaurant that
   * was entered, readable by its own staff.
   *
   * Server-write-only for the obvious reason: a support session able to write
   * its own access record can edit the record of its own access.
   */
  SUPPORT_ACCESS_LOG: "support_access_log",
  // Transfer records — audit log of item/check/server transfers between orders
  ORDER_TRANSFER_RECORDS: "order_transfer_records",
  // Checks — first-class payable bills within orders (5-axis architecture)
  CHECKS: "checks",
  // Check operation audit trail
  CHECK_OPERATIONS: "check_operations",
  // Check idempotency records (separate from audit trail)
  CHECK_IDEMPOTENCY: "check_idempotency",
  /** Platform-wide requests for new ingredients/allergy categories (managed in admin app) */
  PLATFORM_REQUESTS: "platform_requests",
  /** Platform-wide permission groups (managed in admin app, read-only for tenants) */
  PERMISSION_GROUPS: "permission_groups",
  /** Platform-wide permission sub-accesses (managed in admin app, read-only for tenants) */
  PERMISSION_SUB_ACCESSES: "permission_sub_accesses",
  // Payroll — restaurant-scoped collections
  PAYROLL_SETTINGS: "payroll_settings",
  PAY_PERIODS: "pay_periods",
  PAYROLL_RUNS: "payroll_runs",
  PAY_STUBS: "pay_stubs",
  HOLIDAY_SCHEDULES: "holiday_schedules",
  PAYROLL_AUDIT_LOGS: "payroll_audit_logs",
  /**
   * Back-office payroll and missed-punch alerts, one doc per alert on a
   * deterministic id, written by three apps/functions schedulers
   * (pay-period-manager, auto-pay-scheduler, missed-punch-monitor) and
   * DELIVERED to the location's payroll managers by the API cron
   * `/cron/payroll-alerts` (packages/core/src/services/payroll/payrollAlertDelivery.ts),
   * which records `deliveryState` on each doc. Server-only: no rule matches it,
   * so the terminal deny in firestore-rules/00-legacy.rules refuses every client.
   */
  WORKFORCE_ALERTS: "workforce_alerts",
  /**
   * The payroll tax deposit reminder ledger: one doc per (deposit, rung), its id
   * the hex SHA-256 of `${depositKey}|${rung}`, CREATED by the daily API cron
   * `/cron/payroll-deposit-reminders`
   * (packages/core/src/services/payroll/taxDeposits/depositReminderSweep.ts)
   * BEFORE the reminder is sent, so a second run or a retry never sends it
   * twice; it then records `sent` or `failed`. Server-only:
   * firestore-rules/29-payroll-deposit-reminders.rules denies every client.
   */
  PAYROLL_DEPOSIT_REMINDERS: "payroll_deposit_reminders",
  W2_FORMS: "w2_forms",
  W2_GENERATION_RUNS: "w2_generation_runs",
  PAYROLL_DISBURSEMENTS: "payroll_disbursements",
  EMPLOYER_TAX_FORMS: "employer_tax_forms",
  NEC_1099_FORMS: "1099_nec_forms",
  NEW_HIRE_REPORTS: "new_hire_reports",
  TIP_DECLARATIONS: "tip_declarations",
  // Per-check cash tips the till records (cash tip modes §3). Doc id = the
  // till-minted guid (Rule 2). Server-write-only; NOT a payment.
  CASH_TIPS: "cash_tips",
  /** Platform-wide tax tables (shared across all tenants, not per-restaurant) */
  TAX_TABLES: "tax_tables",
  // Gateway binary release metadata (platform-level, written by CI)
  GATEWAY_RELEASES: "gateway_releases",
  // Platform-wide app/OTA configuration (single docs, operator-managed). Holds
  // e.g. the gateway-OTA break-glass trust-policy override.
  APP_CONFIG: "app_config",
  // POS app release metadata (platform-level, written by CI)
  POS_RELEASES: "pos_releases",
  // OrderUp-published starter onboarding guides any location can adopt
  // (platform-level, operator-published). Server-only: denied to every client
  // in firestore.rules and read through the staff onboarding API, matching
  // ONBOARDING_DOCUMENTS. The PDFs live once under the Storage prefix
  // `platform/staff-onboarding-library/`.
  ONBOARDING_DOCUMENT_LIBRARY: "onboarding_document_library",
  // OrderSign processor form templates (TRX's own blank PDFs) and their
  // versions, uploaded and mapped by OrderUp staff in billing. Platform-level
  // and server-only: a version names the Storage object every send reads,
  // under `platform/ordersign-form-templates/`, with no prefix check.
  ORDERSIGN_FORM_TEMPLATES: "ordersign_form_templates",
  ORDERSIGN_FORM_TEMPLATE_VERSIONS: "ordersign_form_template_versions",
  /**
   * Platform bar catalog — OrderUp's curated drink library and the named
   * collections a location adopts onto a menu group. Server-only (rules deny
   * every client); read and mutated through the API.
   */
  BAR_CATALOG_ITEMS: "bar_catalog_items",
  BAR_CATALOG_COLLECTIONS: "bar_catalog_collections",
  BAR_BOTTLES: "bar_bottles",
  /** Platform training-video catalog (server-only). docs/TRAINING_VIDEOS_ARCHITECTURE.md */
  TRAINING_VIDEOS: "training_videos",
  /** Platform training categories (server-only). */
  TRAINING_CATEGORIES: "training_categories",
  /** Platform badge rules (server-only). */
  TRAINING_BADGES: "training_badges",
  /** users/{uid}/training_progress/{videoId} — per-PERSON watch progress (server-only). */
  TRAINING_PROGRESS: "training_progress",
  /** users/{uid}/training_state/summary — per-person rollup (server-only). */
  TRAINING_STATE: "training_state",
  /**
   * users/{uid}/app_installs/{installId} — one doc per mobile-app INSTALL a
   * person runs (staff / management app), keyed by the install id the app
   * generates (Rule 3). Server-written only. docs/MOBILE_APP_VERSIONS.md.
   */
  APP_INSTALLS: "app_installs",
  /**
   * app_install_owners/{installId} — who last reported from an install
   * (`{ uid, app, updatedAt }`), so a report from another person finds the
   * previous one and turns their topic flags off. Top-level, keyed by the
   * install id (Rule 3a). Server-written only. docs/MOBILE_APP_VERSIONS.md §2a.
   */
  APP_INSTALL_OWNERS: "app_install_owners", // scoped-path-ok: not location-scoped (an install belongs to a phone, not a location)
  /**
   * Per-video viewer/completion counters (server-only), keyed by videoId.
   * DERIVED from training_progress via FieldValue.increment in the same
   * transaction — never authored directly, never mirrored (see mirror policy).
   */
  TRAINING_VIDEO_STATS: "training_video_stats",
  /**
   * One announcement per video (server-only), keyed by videoId, with a
   * `training_announcement_deliveries/{uid}` subcollection. PRESERVE in the
   * mirror: a production announcement copied to development would be re-sent.
   */
  TRAINING_ANNOUNCEMENTS: "training_announcements",
  TRAINING_ANNOUNCEMENT_DELIVERIES: "training_announcement_deliveries",
  /**
   * POS Training Mode (docs/superpowers/specs/2026-09-29-pos-training-mode-design.md).
   * Location-scoped practice data. Different ids from orders/checks/payments ON
   * PURPOSE — no trigger or collectionGroup may match them, so practice data can
   * never reach the kitchen, Books, reports or the payment cascade.
   */
  TRAINING_ENROLLMENTS: "training_enrollments",
  TRAINING_SESSIONS: "training_sessions",
  TRAINING_ORDERS: "training_orders",
  TRAINING_CHECKS: "training_checks",
  TRAINING_PAYMENTS: "training_payments",
  /**
   * One ledger row per site-admin bottle import RUN (`bar_bottle_import_runs/{runId}`):
   * the rows each chunk wrote, and whether the run was audited — so a re-run of
   * the final chunk never records the run twice. Server-only.
   */
  BAR_BOTTLE_IMPORT_RUNS: "bar_bottle_import_runs",
  // Remote support sessions (platform-level, cross-tenant)
  SUPPORT_SESSIONS: "support_sessions",
  // Restaurant-level support tickets (AI-triaged help requests)
  SUPPORT_TICKETS: "support_tickets",
  /**
   * The server-assembled evidence bundle hanging off ONE support ticket
   * (`support_tickets/{ticketDocId}/triage_reports/{reportId}`).
   *
   * A SUBCOLLECTION rather than fields on the ticket, because the two carry
   * opposite write rules and one document cannot carry both. The ticket is
   * client-written on purpose — filing one mints nothing. A report is joined
   * from planes that span every tenant (ops.audit API-error clusters, the
   * platform release tables), so it is server-written only; see
   * firestore-rules/40-support-triage-reports.rules.
   */
  SUPPORT_TICKET_TRIAGE_REPORTS: "triage_reports",
  /**
   * Top-level `support_ticket_upload_links/{sha256(token)}` — the short-lived
   * link a POS ticket mints so staff can add phone photos of a receipt or a
   * kitchen chit (docs/superpowers/specs/2026-09-17-pos-support-tickets-design.md
   * §3.2). Keyed by the HASH of the token, never the token. Server-only: no
   * Firestore rule matches it, so the terminal deny refuses every client.
   */
  SUPPORT_TICKET_UPLOAD_LINKS: "support_ticket_upload_links",
  /**
   * Top-level `printer_health_alerts/{deterministicDocId(tenant, location,
   * device, printer)}` — ONE state document per printer for the
   * `printer_health` email: the open-hours clock of its current fault, the
   * send in flight, and which incident was emailed when (the once-per-incident
   * and cooldown rules, `packages/models/src/devices/printerHealthAlert.ts`).
   * Deterministic id + transaction because its writer is an at-least-once
   * scheduled sweep. Server-only: no Firestore rule matches it, so the
   * terminal deny refuses every client.
   */
  PRINTER_HEALTH_ALERTS: "printer_health_alerts",
  /**
   * Top-level `tax_exemption_upload_links/{sha256(token)}` — the short-lived link
   * a till mints when it makes an order tax-exempt, so the purchaser's exemption
   * certificate (front and back) can be photographed with a phone
   * (docs/TAX_EXEMPTION_ARCHITECTURE.md §5). Keyed by the HASH of the token,
   * never the token. Server-only: no Firestore rule matches it, so the terminal
   * deny refuses every client. A Firestore TTL on `expiresAt` removes it.
   */
  TAX_EXEMPTION_UPLOAD_LINKS: "tax_exemption_upload_links",
  /**
   * `orders/{orderId}/late_gratuity_events/{idempotencyKey}` — the append-only
   * audit of every collect / waive of a late auto-gratuity
   * (docs/LATE_AUTO_GRATUITY_ARCHITECTURE.md). Written only by the API with
   * `create()` (the idempotency key is the id); `firestore.rules` lets staff
   * READ it and refuses every client write.
   */
  LATE_GRATUITY_EVENTS: "late_gratuity_events",
  /**
   * Top-level `menu_item_photo_upload_links/{sha256(token)}` — the short-lived
   * link a till's POS menu editor mints so a manager can add a menu item's photo
   * from their phone (docs/POS_MENU_EDITOR.md §8). Keyed by the HASH of the
   * token, never the token. Server-only: an explicit deny rule
   * (`firestore-rules/35-menu-item-photo-upload-links.rules`). A Firestore TTL on
   * `expiresAt` removes it.
   */
  MENU_ITEM_PHOTO_UPLOAD_LINKS: "menu_item_photo_upload_links",
  /**
   * Top-level `checkout_handoffs/{sha256(code)}` — the one-time code a guest's
   * menu page hands checkout so the guest arrives signed in without a token in
   * the URL (`CheckoutHandoffService`). Keyed by the HASH of the code; 2-minute
   * life, consumed on first redemption. Server-only: an explicit deny rule
   * (`firestore-rules/36-checkout-handoffs.rules`). A Firestore TTL on
   * `expiresAt` removes unredeemed ones.
   */
  CHECKOUT_HANDOFFS: "checkout_handoffs",
  // Gift cards and transaction ledger (tenant-level)
  // tenants/{t}/courier_accounts/{providerKey} — the tenant's courier provider
  // account (Uber sub-org / Drive Business). Id = provider key (Rule 3),
  // created atomically; server-write-only (Uber Direct spec 2026-10-27 §6.3).
  COURIER_ACCOUNTS: "courier_accounts",
  GIFT_CARDS: "gift_cards",
  GIFT_CARD_TRANSACTIONS: "gift_card_transactions",
  // Gift card programs — zero or more per tenant, AUTO-ID (a restaurant joins one
  // via settings.giftCards.programId; a card carries the programId it was issued under)
  GIFT_CARD_PROGRAMS: "gift_card_programs",
  // Online gift cards (spec 2026-10-08 §5.1/§5.3). ALL SERVER-ONLY
  // (firestore-rules/80-gift-card-online.rules denies clients and devices).
  // tenants/{t}/gift_card_alerts/{id} — deterministic id per event.
  GIFT_CARD_ALERTS: "gift_card_alerts",
  // tenants/{t}/gift_card_probe_stats/{YYYYMMDDHH} — public lookup misses per hour.
  GIFT_CARD_PROBE_STATS: "gift_card_probe_stats",
  // TOP-LEVEL gift_card_probe_guards/{scope}_{sha256} — per session / device /
  // IP enumeration state (no card numbers; a TTL on expiresAt removes them).
  GIFT_CARD_PROBE_GUARDS: "gift_card_probe_guards",
  // TOP-LEVEL gift_card_key_pins/lookup_root_v{n} — the fingerprint of the
  // gift card lookup root this environment pinned on first use, so a changed
  // root is refused rather than silently orphaning every card's hashes
  // (giftCardKeyRootPin.ts). Server-only; the prod→dev mirror SKIPS it.
  GIFT_CARD_KEY_PINS: "gift_card_key_pins",
  // tenants/{t}/gift_cards/{cardDocId}/holds/{holdDocId} — online checkout
  // reservations (spec §4.2). Server-only; `card.heldCents` is their sum.
  GIFT_CARD_HOLDS: "holds",
  // tenants/{t}/gift_card_online_tenders/{orderGuid} — an online order's gift
  // card leg, written at capture; the cancel/deny reversal reads it.
  GIFT_CARD_ONLINE_TENDERS: "gift_card_online_tenders",
  // Gift card imports from a merchant's previous POS (auto-ID), one row doc per
  // source card beneath. SERVER-ONLY: rows carry full card numbers.
  GIFT_CARD_IMPORTS: "gift_card_imports",
  GIFT_CARD_IMPORT_ROWS: "gift_card_import_rows",
  // Audit log of every decrypt of a gift card's full number (reprint, e-gift
  // resend, manager reveal — spec 2026-10-08 §2.1). Auto-ID, append-only,
  // SERVER-ONLY: no client rule, so clients and devices are denied.
  GIFT_CARD_NUMBER_REVEALS: "gift_card_number_reveals",
  // Audit log of every gift card PIN set / reset / unlock (spec 2026-10-08
  // §13). Auto-ID, append-only, SERVER-ONLY: never holds a PIN or its hash.
  GIFT_CARD_PIN_EVENTS: "gift_card_pin_events",
  // Per-tenant progress + verification of the number-protection backfill
  // (spec 2026-10-08 §2.3, slice 0b): one doc, `state`. SERVER-ONLY. Slices
  // 0c/0d refuse to act on a tenant whose stored verification did not pass.
  GIFT_CARD_NUMBER_BACKFILL: "gift_card_number_backfill",
  // E-gift orders (spec 2026-10-08 §6): one per online purchase / POS digital
  // delivery, id = the buyer-payment idempotency key. SERVER-ONLY, reads included.
  EGIFT_ORDERS: "egift_orders",
  // Daily e-gift purchase totals per buyer email / payment card (§5.4 caps).
  // Id `${day}_${kind}_${sha256(key)}`. SERVER-ONLY.
  EGIFT_PURCHASE_COUNTERS: "egift_purchase_counters",
  // TOP-LEVEL provider message id → { tenantDocId, orderId }, so a bounce event
  // (which names only the message) finds its e-gift order. SERVER-ONLY.
  EGIFT_DELIVERY_MESSAGES: "egift_delivery_messages",
  // Customer accounts (public ordering customers, separate from staff Users)
  CUSTOMERS: "customers",
  // Server-only payment-credential vault — one doc per restaurant, keyed by the
  // restaurant's (globally-unique) Firestore docId. Holds the ENCRYPTED gateway
  // secrets (TRX password and per-account credentials) OFF the public restaurant doc,
  // so a raw client-SDK / unauthenticated read of the restaurant can never
  // expose them. TOP-LEVEL (not a subcollection) so no `{path=**}` rule under a
  // location document can ever reach it. Admin-SDK access only; rules deny all.
  PAYMENT_CREDENTIALS: "payment_credentials",
  // Server-only per-location MEASUREMENT secrets (GA4 Measurement Protocol
  // api_secret, Meta Conversions API token) — one doc per location, doc id =
  // the location guid. TOP-LEVEL and Admin-SDK only (rules deny all): these
  // are not `integrations` rows because that collection is readable by every
  // tenant member's browser and is enumerated by marketplace sync workers.
  MEASUREMENT_SECRETS: "measurement_secrets",
  // Per-ORDER ad attribution + per-destination send stamps for server-side
  // purchase measurement (GA4 Measurement Protocol, Meta Conversions API).
  // LOCATION-SCOPED subcollection, doc id = the order guid (Rule 3: the
  // request path and the checkout finisher both write it). Admin-SDK only
  // (rules deny all): it holds guest browser ids, IP and user agent. Never on
  // the order itself — the POS write-back drops unknown order fields.
  ORDER_MEASUREMENTS: "order_measurements",
  /** Card-linked loyalty index: keyed card identity → opted-in customer. Top-level, Admin-SDK only. */
  CARD_IDENTITIES: "card_identities",
  // TOP-LEVEL card_identity_key_pins/hmac_key_ci{n} — the fingerprint of the
  // CARD_IDENTITY_HMAC_KEY this environment pinned on first use, so a changed
  // secret mints no key rather than silently orphaning every card link
  // (cardIdentityKeyPin.ts). Server-only; the prod→dev mirror SKIPS it.
  CARD_IDENTITY_KEY_PINS: "card_identity_key_pins",
  /** Wallet pass tokens (Apple VAS / Google Smart Tap), one per customer. Top-level, Admin-SDK only. */
  WALLET_PASSES: "wallet_passes",
  /** Additional TRX merchant accounts (MIDs) of one location — subcollection of payment_credentials/{locationDocId}. Admin-SDK only. */
  PAYMENT_CREDENTIAL_MERCHANT_ACCOUNTS: "merchant_accounts",
  // Platform configuration (top-level singleton docs)
  PLATFORM_CONFIG: "platform_config",
  // MDM enrollment tokens (restaurant-scoped)
  MDM_ENROLLMENT_TOKENS: "mdm_enrollment_tokens",
  // MDM policies (platform-level, under platform_config)
  MDM_POLICIES: "mdm_policies",
  // MDM device command audit log (platform-level, under platform_config)
  MDM_COMMANDS: "mdm_commands",
  // MDM enrollments awaiting device registration (keyed by hardware serial)
  MDM_PENDING_ENROLLMENTS: "mdm_pending_enrollments",
  // Fleet management (self-managed Device-Owner control plane): per-device
  // authoritative command subcollection under a restaurant device doc.
  DEVICE_COMMANDS: "commands",
  // Fleet management: derived operator read-model (platform-level ledger).
  FLEET_COMMANDS: "fleet_commands",
  // Managed-router VPN access plane: operator L3 access grants (the audit SSOT
  // for who could reach which store's management network, and until when).
  VPN_GRANTS: "vpn_grants",
  // Managed-router: versioned, signed NetworkConfig documents per restaurant
  // (docs/GATEWAY_MANAGED_ROUTER_ARCHITECTURE.md §4). Auto-ID document, with
  // the monotonic version in the body and allocated by a query inside the
  // write transaction — Rule 1 (docs/FIRESTORE_DOCUMENT_IDS.md), the house
  // convention every other entity follows. This comment used to say "Doc ID =
  // the version"; that was the FIRST implementation, replaced deliberately
  // (networkConfigService.ts, networkConfigConventions.test.ts), and nothing
  // addresses a config by its version. Documents stay immutable once
  // published (a change is a new version, never an edit).
  NETWORK_CONFIGS: "network_configs",
  // Managed-router secret VALUES, keyed by the ref the NetworkConfig document
  // carries (today: the VRRP shared secret). Deliberately a SEPARATE
  // collection, never a field on the config: that document is signed, kept as
  // last-known-good, and rendered into PCI evidence packs handed to auditors —
  // invariant 1 is that it carries refs and never values. Server-side reads
  // only; no browser is ever granted access, and the value leaves the platform
  // exactly once, to the device-authenticated gateway that must install it.
  NETWORK_SECRETS: "network_secrets",
  // Weather forecasts (cached per-restaurant, refreshed by scheduled Cloud Function)
  WEATHER_FORECASTS: "weather_forecasts",
  // Instagram feed (cached per-restaurant, refreshed by the instagramFeedSync
  // Cloud Function; public-read on the restaurant website, Admin-SDK writes only)
  INSTAGRAM_FEED: "instagram_feed",
  /**
   * The operational-retention enforcer's heartbeat and per-location state
   * (apps/functions `operationalRetentionSweep`): `latest` holds the last run's
   * totals; one document per location (`{tenantDocId}__{locationDocId}`, Rule
   * 3a — the location IS the identity) holds its rolling per-node baseline for
   * the anomaly guard and its last counts. Platform-level and server-only.
   * docs/superpowers/specs/2026-10-05-operational-data-lifecycle-design.md.
   */
  OPERATIONAL_RETENTION_RUNS: "operational_retention_runs",
  // Appointments — appointment-style scheduling for service businesses.
  // Restaurant subcollections. See packages/models/src/appointments/*.
  // Service catalog (name, price cents, duration, buffers, deposit override).
  BOOKABLE_SERVICES: "bookable_services",
  // Per-employee booking profile (booking hours, service overrides, time-off).
  PROVIDER_BOOKING_PROFILES: "provider_booking_profiles",
  // Booked service slots (the appointments themselves).
  APPOINTMENTS: "appointments",
  // Per-provider day index of booked/held intervals for conflict checks.
  APPOINTMENT_DAYS: "appointment_days",

  // Anonymous item feedback — a restaurant trials a dish, prints a QR card, and
  // guests answer four fixed questions anonymously. NOT menu items: these carry
  // no price, never appear on a check, and link to a real menu item only via an
  // optional `menuItemGuid`. All FOUR are SERVER-WRITE-ONLY (no firestore.rules
  // entry, so they fall through to the deny-all backstop).
  // One document per ROUND of feedback on one thing being trialled.
  FEEDBACK_ITEMS: "feedback_items",
  // One printed card carrying SEVERAL rounds behind ONE QR — a Sunday special
  // with three dishes on it. Holds membership and order only: no rollup, no
  // responses, no offer type. Every answer still lands on the ROUND the guest
  // picked, under that round's own token.
  FEEDBACK_CARDS: "feedback_cards",
  // One anonymous guest answer. Carries no identifier of any kind — see
  // packages/models/src/feedback/FeedbackResponse.ts for the absence contract.
  FEEDBACK_RESPONSES: "feedback_responses",
  // TOP-LEVEL. Document id IS the printed token; the doc carries the tenant /
  // restaurant scope so `/f/{token}` resolves with a doc-id read instead of a
  // collection-group query (which would need a COLLECTION_GROUP fieldOverride
  // Firestore does not create automatically, and would open a cross-tenant
  // query surface for a value printed on a card). One pointer names EITHER a
  // round or a card — `kind` says which, and absent means `item`.
  FEEDBACK_TOKENS: "feedback_tokens",
  // Guest codes — one printed QR per placement; destination resolved at scan
  // time by /q/:token. SERVER-WRITE-ONLY (no firestore.rules entry → deny-all).
  GUEST_CODES: "guest_codes",
  // TOP-LEVEL. Document id IS the printed token (same reasoning as
  // FEEDBACK_TOKENS: a doc-id read, never a collection-group query).
  GUEST_CODE_TOKENS: "guest_code_tokens",
  // Guest service requests — "call server" / "request the bill" pressed on the
  // guest table page. SERVER-WRITE-ONLY (no firestore.rules entry → deny-all):
  // the guest page holds no Firestore credential, and the API is the only thing
  // that knows whether the feature is on, whether the table is real, and
  // whether this table has spent its daily ceiling.
  SERVICE_REQUESTS: "service_requests",
  // Per-table, per-UTC-day ceiling and open-request pointer for the above.
  // Doc id = `{tableGuid}_{YYYY-MM-DD}` (see serviceRequestCounterDocId in
  // @order-up/models), so yesterday's counter resets by being a document
  // nothing reads again. Server-write-only, same reasoning.
  SERVICE_REQUEST_COUNTERS: "service_request_counters",
  // Money-movement ledger for the appointments vertical (deposit charges,
  // no-show fee charges, deposit refunds). Doc id = the leg's idempotency key
  // (`appt_{guid}_{leg}`, Rule 3b) so a retried charge lands on the SAME
  // record instead of duplicating — the write is an atomic create inside the
  // recording transaction, NOT a merge, and a retry after a decline replaces
  // the failed record while keeping it under an auto-ID. Server-write-only via
  // the Admin SDK. See packages/models/src/appointments/AppointmentPaymentRecord.
  APPOINTMENT_PAYMENTS: "appointment_payments",
  // Customer membership program enrollment — subcollection of a customer doc
  // (customers/{uid}/memberships). See packages/models/src/customers/CustomerMembership.
  MEMBERSHIPS: "memberships",
  // Customer FCM push-token registry — subcollection of a customer doc
  // (customers/{uid}/push_tokens). Server-write-only via the API (sealed in
  // firestore.rules); doc id = sha256(token) hex. See
  // packages/core/src/services/customerPushTokenService.ts.
  PUSH_TOKENS: "push_tokens",
  // Top-level, server-write-only claims that an OrderUp billing notice went
  // out ONCE (doc id = `auto_enroll_<location guid>` for the online-order fee
  // auto-enrollment email). Atomic `create()` is the dedupe; no client rule
  // matches, so every client read and write is refused. See
  // packages/core/src/services/billing/onlineOrderFeeNoticeClaims.ts.
  ONLINE_ORDER_FEE_NOTICES: "online_order_fee_notices",
} as const;

/**
 * Deterministic document IDs for collections that hold exactly one
 * document per parent. Centralizing here means the write site and the
 * read site cannot drift — a misnamed primary doc is invisible to the
 * read, which would silently route every transaction to the hard
 * fallback. This is the kind of bug that only shows up in production
 * because dev-fallback config "happens" to work.
 *
 * Do not describe consumers here; each constant's own doc comment names the
 * rule, and the consumers are whatever imports it.
 */
export const CANONICAL_DOC_IDS = {
  /**
   * The two system items (Open Food / Open Drink) — exactly one of each per
   * restaurant, Rule 3c (docs/FIRESTORE_DOCUMENT_IDS.md).
   *
   * `ensureSystemItems` is called from the management items page on every mount,
   * so two tabs on a new restaurant both saw "missing" and both created; the
   * only thing refusing the second was `createItem`'s name check, which is
   * itself a query-then-create. The POS resolves these by `systemItemType`, not
   * by id, so a duplicate is not an error anywhere — it is just two Open Food
   * buttons on the till.
   */
  SYSTEM_ITEM_OPEN_FOOD: "system_open_food",
  SYSTEM_ITEM_OPEN_DRINK: "system_open_drink",
  /**
   * The single triage report per support ticket.
   *
   * Deterministic rather than generated so that re-assembling REPLACES the
   * report instead of appending a second one — which is what makes both the
   * operator's re-run and the nightly backfill safe to run twice. It is also
   * the only way `has a report already?` stays a one-document read: the sweep
   * asks that question once per candidate ticket, and a generated id would
   * turn each of those into a collection query.
   */
  SUPPORT_TICKET_TRIAGE_REPORT_PRIMARY: "current",
  /**
   * The single close plan per cash-drawer session (A8b/A8d). Deterministic so
   * the claim transaction reads the ONE document a concurrent close would
   * write, and a second plan can never exist beside the first.
   */
  CASH_DRAWER_CLOSE_PLAN_PRIMARY: "current",
  /**
   * The one totals document of a physical bar count (bar v2 §C2), under
   * `COLLECTIONS.INVENTORY_COUNT_SUMMARY`. Deterministic so a second submit of
   * the same count meets the first one's `create()` and is refused.
   */
  INVENTORY_COUNT_SUMMARY_TOTALS: "totals",
} as const;

/**
 * `systemItemType` → its canonical `items` document id. Keyed by the type, so a
 * third system item cannot be added without giving it an id.
 */
export const SYSTEM_ITEM_DOC_IDS = {
  open_food: CANONICAL_DOC_IDS.SYSTEM_ITEM_OPEN_FOOD,
  open_drink: CANONICAL_DOC_IDS.SYSTEM_ITEM_OPEN_DRINK,
} as const;

// Location-scoped helpers delegate to the location scope resolver
// (../scope/locationScope), so the legacy tree segment is spelled in ONE place.
const scopeOf = (tenantDocId: string, locationDocId: string): LocationScope => ({
  tenantDocId,
  locationDocId,
});

/**
 * The audit trails under `users/{uid}` that OUTLIVE the user. Deleting a user
 * removes the user document and every OTHER subcollection; these two stay,
 * unchanged, at `users/{uid}/<id>` — readable by path, reached from the
 * deletion manifest's uid (DATA_RETENTION_AND_DELETION_POLICY.md §1.6). They
 * hold no name, email or phone: the uid is their only link to the person, and
 * the manifest is where that link resolves.
 *
 * Read by `UserService.deleteUser` (the `retain` list of its recursive delete)
 * and written into every deletion manifest (`retainedAuditTrails`).
 */
export const RETAINED_USER_AUDIT_TRAILS = [
  COLLECTIONS.ASSIGNMENT_HISTORY,
  COLLECTIONS.MFA_AUDIT,
] as const;

// Simple collection path generation for top-level collections
export const CollectionPaths = {
  // Top-level collections (no parent document resolution needed)
  admins: (): string => COLLECTIONS.ADMINS,
  smsOptOuts: (): string => COLLECTIONS.SMS_OPT_OUTS,
  siteRoleFloors: (): string => COLLECTIONS.SITE_ROLE_FLOORS,
  users: (): string => COLLECTIONS.USERS,
  assignmentHistory: (uid: string): string =>
    `${COLLECTIONS.USERS}/${uid}/${COLLECTIONS.ASSIGNMENT_HISTORY}`,
  /** Second factors OrderUp owns. Server-write-only; holds AES-GCM ciphertext. */
  mfaFactors: (uid: string): string =>
    `${COLLECTIONS.USERS}/${uid}/${COLLECTIONS.MFA_FACTORS}`,
  trainingProgress: (uid: string): string =>
    `${COLLECTIONS.USERS}/${uid}/${COLLECTIONS.TRAINING_PROGRESS}`,
  trainingState: (uid: string): string =>
    `${COLLECTIONS.USERS}/${uid}/${COLLECTIONS.TRAINING_STATE}`,
  /** Mobile app installs for one person. Server-write-only. */
  appInstalls: (uid: string): string =>
    `${COLLECTIONS.USERS}/${uid}/${COLLECTIONS.APP_INSTALLS}`,
  /** Who last reported from each install (`{installId}`). Server-write-only. */
  appInstallOwners: (): string => COLLECTIONS.APP_INSTALL_OWNERS,
  /** Live step-up markers, keyed by session id. Server-write-only. */
  mfaStepUps: (uid: string): string =>
    `${COLLECTIONS.USERS}/${uid}/${COLLECTIONS.MFA_STEP_UPS}`,
  /** Append-only second-factor audit trail. Server-write-only. */
  mfaAudit: (uid: string): string =>
    `${COLLECTIONS.USERS}/${uid}/${COLLECTIONS.MFA_AUDIT}`,
  deletionManifests: (): string => COLLECTIONS.DELETION_MANIFESTS,
  verifiedEmailIndex: (): string => COLLECTIONS.VERIFIED_EMAIL_INDEX,
  systemRoles: (): string => COLLECTIONS.SYSTEM_ROLES,
  allergyCategories: (): string => COLLECTIONS.ALLERGY_CATEGORIES,
  ingredients: (): string => COLLECTIONS.INGREDIENTS,
  businessTypes: (): string => COLLECTIONS.BUSINESS_TYPES,
  businessSubCategories: (): string => COLLECTIONS.BUSINESS_SUB_CATEGORIES,
  // Cross-tenant business connections (Phase 1 "Accountant Access").
  businessConnections: (): string => COLLECTIONS.BUSINESS_CONNECTIONS,
  platformRequests: (): string => COLLECTIONS.PLATFORM_REQUESTS,
  permissionGroups: (): string => COLLECTIONS.PERMISSION_GROUPS,
  permissionSubAccesses: (): string => COLLECTIONS.PERMISSION_SUB_ACCESSES,
  customers: (): string => COLLECTIONS.CUSTOMERS,
  tenants: (): string => COLLECTIONS.TENANTS,
  // Server-only encrypted payment-credential vault, one doc per restaurant
  // (docId = the restaurant's Firestore docId). Admin-SDK only; rules deny all.
  paymentCredentials: (): string => COLLECTIONS.PAYMENT_CREDENTIALS,
  measurementSecrets: (): string => COLLECTIONS.MEASUREMENT_SECRETS,
  // Card-linked loyalty (docs/CARD_LINKED_LOYALTY.md). Top-level and
  // cross-tenant by design; docId = the card identity key (`ci1_…`).
  cardIdentities: (): string => COLLECTIONS.CARD_IDENTITIES,
  cardIdentityKeyPins: (): string => COLLECTIONS.CARD_IDENTITY_KEY_PINS,
  walletPasses: (): string => COLLECTIONS.WALLET_PASSES,
  paymentCredentialsDoc: (locationDocId: string): string =>
    `${COLLECTIONS.PAYMENT_CREDENTIALS}/${locationDocId}`,
  // Additional TRX merchant accounts (MIDs) of one location; the location's
  // original credential stays on the parent doc as the `primary` account.
  // docId = the server-minted account id (`acct_…`). Admin-SDK only.
  paymentCredentialMerchantAccounts: (locationDocId: string): string =>
    `${COLLECTIONS.PAYMENT_CREDENTIALS}/${locationDocId}/${COLLECTIONS.PAYMENT_CREDENTIAL_MERCHANT_ACCOUNTS}`,
  /** @deprecated Phase 11 — use `domains()`. Removal target: 2026-07-26. */
  emailIdentities: (): string => COLLECTIONS.EMAIL_IDENTITIES,
  domains: (): string => COLLECTIONS.DOMAINS,
  feedbackTokens: (): string => COLLECTIONS.FEEDBACK_TOKENS,
  guestCodeTokens: (): string => COLLECTIONS.GUEST_CODE_TOKENS,
  integrations: (): string => COLLECTIONS.INTEGRATIONS,
  aiContentDrafts: (): string => COLLECTIONS.AI_CONTENT_DRAFTS,
  deliveryReportJobs: (): string => COLLECTIONS.DELIVERY_REPORT_JOBS,
  invitations: (): string => COLLECTIONS.INVITATIONS,
  restaurantClaimCodes: (): string => COLLECTIONS.RESTAURANT_CLAIM_CODES,
  restaurantClaimRedemptions: (): string =>
    COLLECTIONS.RESTAURANT_CLAIM_REDEMPTIONS,
  gatewayReleases: (): string => COLLECTIONS.GATEWAY_RELEASES,
  appConfig: (): string => COLLECTIONS.APP_CONFIG,
  posReleases: (): string => COLLECTIONS.POS_RELEASES,
  /** Code-push patches on one release — written by the POS CI (record-patch.sh). */
  posReleasePatches: (version: string): string =>
    `${COLLECTIONS.POS_RELEASES}/${version}/patches`,
  onboardingDocumentLibrary: (): string =>
    COLLECTIONS.ONBOARDING_DOCUMENT_LIBRARY,
  orderSignFormTemplates: (): string => COLLECTIONS.ORDERSIGN_FORM_TEMPLATES,
  orderSignFormTemplateVersions: (): string =>
    COLLECTIONS.ORDERSIGN_FORM_TEMPLATE_VERSIONS,
  barCatalogItems: (): string => COLLECTIONS.BAR_CATALOG_ITEMS,
  barCatalogCollections: (): string => COLLECTIONS.BAR_CATALOG_COLLECTIONS,
  barBottles: (): string => COLLECTIONS.BAR_BOTTLES,
  trainingVideos: (): string => COLLECTIONS.TRAINING_VIDEOS,
  trainingCategories: (): string => COLLECTIONS.TRAINING_CATEGORIES,
  trainingBadges: (): string => COLLECTIONS.TRAINING_BADGES,
  trainingVideoStats: (): string => COLLECTIONS.TRAINING_VIDEO_STATS,
  trainingAnnouncements: (): string => COLLECTIONS.TRAINING_ANNOUNCEMENTS,
  trainingAnnouncementDeliveries: (videoId: string): string =>
    `${COLLECTIONS.TRAINING_ANNOUNCEMENTS}/${videoId}/${COLLECTIONS.TRAINING_ANNOUNCEMENT_DELIVERIES}`,
  supportSessions: (): string => COLLECTIONS.SUPPORT_SESSIONS,

  // Tenant subcollections (single parent: tenant)
  rolePermissions: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.ROLE_PERMISSIONS}`,

  // Subcollection paths that require parent document ID resolution
  // These functions expect the actual Firestore document ID, not business ID
  restaurants: (tenantDocId: string): string =>
    tenantLocationsPath(tenantDocId),

  // Restaurant subcollections - Brands (marketplace-facing identities)
  // Doc id IS the brand guid, i.e. the KitchenHub `partner_store_id`.
  brands: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.BRANDS),

  // Restaurant subcollections - Menu Management
  // These require both tenant and restaurant Firestore document IDs
  menuGroups: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.MENU_GROUPS),

  // Restaurant subcollections - Digital menu boards (signage)
  menuBoards: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.MENU_BOARDS),

  // Immutable published versions of ONE board, under that board's document.
  // Written only by publish, never updated, and deleted only with the board
  // itself. This is the collection an appliance's fetch path reads — see
  // MENU_BOARD_VERSIONS above for why the draft cannot live here.
  menuBoardVersions: (
    tenantDocId: string,
    locationDocId: string,
    boardId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.MENU_BOARDS, boardId)}/${COLLECTIONS.MENU_BOARD_VERSIONS}`,

  // Restaurant subcollections — OrderUp Studio designs (the design canvas)
  designs: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.DESIGNS),

  // Immutable published versions of ONE design, under that design's document.
  // Written only by publish, never updated, deleted only with the design.
  designVersions: (
    tenantDocId: string,
    locationDocId: string,
    designId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.DESIGNS, designId)}/${COLLECTIONS.DESIGN_VERSIONS}`,

  // Restaurant subcollections — printed menus (handouts built from the menu)
  printedMenus: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PRINTED_MENUS),

  // Immutable saved versions of ONE printed menu, under its document.
  printedMenuVersions: (
    tenantDocId: string,
    locationDocId: string,
    printedMenuId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PRINTED_MENUS, printedMenuId)}/${COLLECTIONS.PRINTED_MENU_VERSIONS}`,

  // The email sends of ONE printed menu's versions, under its document.
  printedMenuDeliveries: (
    tenantDocId: string,
    locationDocId: string,
    printedMenuId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PRINTED_MENUS, printedMenuId)}/${COLLECTIONS.PRINTED_MENU_DELIVERIES}`,

  revenueCenters: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.REVENUE_CENTERS),

  cateringLeads: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CATERING_LEADS),

  cateringOrders: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CATERING_ORDERS),

  cateringSettings: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CATERING_SETTINGS),

  advertisingRequests: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.ADVERTISING_REQUESTS),

  items: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.ITEMS),

  courses: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.COURSES),

  modifierGroups: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.MODIFIER_GROUPS),

  salesCategories: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SALES_CATEGORIES),

  discounts: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.DISCOUNTS),

  taxRates: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TAX_RATES),

  tags: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TAGS),

  voidReasons: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.VOID_REASONS),

  modifiers: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.MODIFIERS),

  // RecipeOps (Phase 14-R)
  recipeItems: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.RECIPE_ITEMS),

  barAdoptions: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.BAR_ADOPTIONS),
  /** The wine list's open bottle per wine (Layer 2), keyed by inventory guid. Server-written only. */
  barOpenBottles: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.BAR_OPEN_BOTTLES),
  /** One marker per sent wine glass line, keyed by order item guid — a replayed send never pours twice. */
  barOpenBottlePours: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.BAR_OPEN_BOTTLE_POURS),

  inventoryItems: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.INVENTORY_ITEMS),

  modifierIngredients: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.MODIFIER_INGREDIENTS),

  recipeopsDecrements: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.RECIPEOPS_DECREMENTS),

  recipeopsRestocks: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.RECIPEOPS_RESTOCKS),

  stockDecrements: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.STOCK_DECREMENTS),

  lowStockAlerts: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.LOW_STOCK_ALERTS),

  paperSupply: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAPER_SUPPLY),

  paperSupplyEvents: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAPER_SUPPLY_EVENTS),

  supplyOffers: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SUPPLY_OFFERS),

  /**
   * Physical bar counts (bar v2 design 2026-09-30 §C2) — APPEND-ONLY: header,
   * `inventory_count_lines/{inventoryGuid}` and `inventory_count_summary/totals` are created once and never
   * updated or deleted (the `audit-append-only` guard holds every server write
   * to `create()`; rules deny clients). Deliberately NOT a `COLLECTIONS`
   * constant: the prod→dev mirror's compile-time policy check keys off
   * `COLLECTIONS`, and that guard forbids a mirror module from naming an
   * append-only collection at all.
   */
  inventoryCounts: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), "inventory_counts"),

  /** `inventory_counts/{countGuid}/inventory_count_lines` — one line per counted item (append-only). */
  inventoryCountLines: (tenantDocId: string, locationDocId: string, countGuid: string): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), "inventory_counts", countGuid)}/${COLLECTIONS.INVENTORY_COUNT_LINES}`,

  /** `inventory_counts/{countGuid}/inventory_count_summary` — holds the one totals doc (append-only). */
  inventoryCountSummary: (tenantDocId: string, locationDocId: string, countGuid: string): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), "inventory_counts", countGuid)}/${COLLECTIONS.INVENTORY_COUNT_SUMMARY}`,

  inventoryCountPostings: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.INVENTORY_COUNT_POSTINGS),

  // Restaurant subcollections - Staff Management
  employees: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.EMPLOYEES),

  // Manager-only sensitive comp/PII subcollection under a single employee doc.
  // Documents here are read-gated to managers/owners/admins/POS-device + self
  // and written only by the Admin SDK (firestore.rules). See SOC2 #48/H-DP2.
  employeePrivate: (
    tenantDocId: string,
    locationDocId: string,
    employeeDocId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.EMPLOYEES, employeeDocId)}/${COLLECTIONS.EMPLOYEE_PRIVATE}`,

  // Manager-approval handshake queue for direct-deposit self-service changes,
  // nested under a single employee doc. Admin-SDK-only writes; documents hold
  // only masked display fields + the encrypted account payload (never raw PII).
  employeeDirectDepositRequests: (
    tenantDocId: string,
    locationDocId: string,
    employeeDocId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.EMPLOYEES, employeeDocId)}/${COLLECTIONS.DIRECT_DEPOSIT_REQUESTS}`,

  // Restaurant subcollections - Customers (W4)
  customerProfiles: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CUSTOMER_PROFILES),

  loyaltyPrograms: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.LOYALTY_PROGRAMS),

  // Append-only loyalty events — nested under a customer profile so events
  // are naturally scoped to (tenant, restaurant, customer) for query + rules.
  loyaltyEvents: (
    tenantDocId: string,
    locationDocId: string,
    customerUid: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CUSTOMER_PROFILES, customerUid)}/${COLLECTIONS.LOYALTY_EVENTS}`,

  // Server-only customer card-on-file refs — nested under a customer profile
  // so a saved card is naturally scoped to (tenant, restaurant, customer).
  // Doc id == methodId. Admin-SDK-only writes (rules deny client writes).
  savedPaymentMethods: (
    tenantDocId: string,
    locationDocId: string,
    customerUid: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CUSTOMER_PROFILES, customerUid)}/${COLLECTIONS.SAVED_PAYMENT_METHODS}`,

  // Server-only per-order accrual idempotency markers — nested under a
  // customer profile so a marker is naturally scoped to (tenant, restaurant,
  // customer). Doc id == orderId. Admin-SDK-only writes (rules deny all).
  loyaltyOrderMarkers: (
    tenantDocId: string,
    locationDocId: string,
    customerUid: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CUSTOMER_PROFILES, customerUid)}/${COLLECTIONS.LOYALTY_ORDER_MARKERS}`,

  // Top-level customer merge audit (global identity merges).
  customerMerges: (): string => COLLECTIONS.CUSTOMER_MERGES,

  /**
   * Receipt claims for a restaurant (customer "claim a missed purchase"
   * records — Part F of the receipts/accounts decoupling).
   */
  receiptClaims: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.RECEIPT_CLAIMS),

  // Card-fingerprint index — restaurant-scoped POS-runtime identity lookup.
  // Document id is the SHA-256 hex of normalized (BIN + last4 + expiry_month).
  cardFingerprints: (
    tenantDocId: string,
    locationDocId: string,
  ): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CARD_FINGERPRINTS),

  jobTitles: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.JOB_TITLES),

  onboardingDocuments: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.ONBOARDING_DOCUMENTS),

  jobPermissions: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.JOB_PERMISSIONS),

  departments: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.DEPARTMENTS),

  laborWarnings: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.LABOR_WARNINGS),

  schedules: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SCHEDULES),

  shiftRequests: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SHIFT_REQUESTS),

  shifts: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SHIFTS),

  shiftTasks: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SHIFT_TASKS),

  shiftTaskCompletions: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SHIFT_TASK_COMPLETIONS),

  timeEntries: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TIME_ENTRIES),

  shiftAlerts: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SHIFT_ALERTS),

  attendanceRecords: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.ATTENDANCE_RECORDS),

  shiftAlertSubscribers: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SHIFT_ALERT_SUBSCRIBERS),

  employeeDataRequests: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.EMPLOYEE_DATA_REQUESTS),

  employeeTextConsents: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.EMPLOYEE_TEXT_CONSENTS),

  timeclockConsents: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TIMECLOCK_CONSENTS),
  stationCheckouts: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.STATION_CHECKOUTS),

  // Restaurant subcollections - Staff/Management communication
  // Staff-app team chat (employees + managers within a restaurant).
  teamMessages: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TEAM_MESSAGES),

  // Industry-grade team chat (chat_v2).
  chatChannels: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CHAT_CHANNELS),
  chatChannelMembers: (
    tenantDocId: string,
    locationDocId: string,
    channelId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CHAT_CHANNELS, channelId)}/${COLLECTIONS.CHAT_MEMBERS}`,
  chatMessages: (
    tenantDocId: string,
    locationDocId: string,
    channelId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CHAT_CHANNELS, channelId)}/${COLLECTIONS.CHAT_MESSAGES}`,
  chatMessageAcks: (
    tenantDocId: string,
    locationDocId: string,
    channelId: string,
    messageId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CHAT_CHANNELS, channelId)}/${COLLECTIONS.CHAT_MESSAGES}/${messageId}/${COLLECTIONS.CHAT_MESSAGE_ACKS}`,
  chatReaderUids: (
    tenantDocId: string,
    locationDocId: string,
    channelId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CHAT_CHANNELS, channelId)}/${COLLECTIONS.CHAT_READER_UIDS}`,
  chatUserState: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CHAT_USER_STATE),

  // Management-app manager's log (shift-handoff entries, manager-authored).
  managerLogEntries: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.MANAGER_LOG_ENTRIES),

  // Management-app pre-shift briefing notes (shown to staff before a shift).
  preShiftNotes: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PRE_SHIFT_NOTES),

  // Restaurant subcollections - PIN Authentication
  pinLookup: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PIN_LOOKUP),

  pinThrottle: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PIN_THROTTLE),

  pinAuditLogs: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PIN_AUDIT_LOGS),

  // Restaurant subcollections - POS Training Mode
  trainingEnrollments: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TRAINING_ENROLLMENTS),
  trainingSessions: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TRAINING_SESSIONS),
  trainingOrders: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TRAINING_ORDERS),
  trainingChecks: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TRAINING_CHECKS),
  trainingPayments: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TRAINING_PAYMENTS),

  // Restaurant subcollections - Kitchen Management
  printers: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PRINTERS),

  paymentTerminals: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAYMENT_TERMINALS),

  prepStations: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PREP_STATIONS),

  // Cash drawer ENTITY documents — one doc per physical drawer, doc id = guid.
  cashDrawers: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CASH_DRAWERS),

  // Cash-management EVENT log (deposits, pay-outs, floats, variances, no-sales)
  // — one doc per movement, doc id = eventId (the Books idempotency key).
  cashDrawerEvents: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CASH_DRAWER_EVENTS),

  // Card holds (server-only; docs/CARD_HOLDS.md). Doc id = holdId.
  cardHolds: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CARD_HOLDS),

  // Cash-drawer shift sessions (CD-7) — the server-side ledger of record.
  cashDrawerSessions: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CASH_DRAWER_SESSIONS),

  // The close-plan collection under ONE session (server-only). Its single
  // document is CANONICAL_DOC_IDS.CASH_DRAWER_CLOSE_PLAN_PRIMARY — see
  // `cashDrawerClosePlan` for the full document path.
  cashDrawerClosePlans: (
    tenantDocId: string,
    locationDocId: string,
    sessionId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CASH_DRAWER_SESSIONS, sessionId)}/${COLLECTIONS.CASH_DRAWER_CLOSE_PLAN}`,

  // The close-plan DOCUMENT of one session.
  cashDrawerClosePlan: (
    tenantDocId: string,
    locationDocId: string,
    sessionId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CASH_DRAWER_SESSIONS, sessionId)}/${COLLECTIONS.CASH_DRAWER_CLOSE_PLAN}/${CANONICAL_DOC_IDS.CASH_DRAWER_CLOSE_PLAN_PRIMARY}`,

  // Kitchen buzzer ENTITY documents — one doc per physical buzzer, doc id = guid.
  kitchenBuzzers: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.KITCHEN_BUZZERS),

  // Restaurant subcollections - System Management
  counters: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.COUNTERS),

  // Platform-level collections - System Management
  platformCounters: (): string => COLLECTIONS.COUNTERS,
  siteConfiguration: (): string => COLLECTIONS.SITE_CONFIGURATION,

  // Restaurant subcollections - Reviews from multiple sources
  reviews: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.REVIEWS),

  // Restaurant subcollection - Google Business Profile post drafts (owner approves each)
  googleBusinessPostDrafts: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.GOOGLE_BUSINESS_POST_DRAFTS),

  // Restaurant subcollections - Media (photos/videos) from multiple sources
  media: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.MEDIA),

  // Restaurant subcollections - Orders and Carts
  orders: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.ORDERS),

  carts: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CARTS),

  // Restaurant subcollections - Payment Records
  payments: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAYMENTS),

  // Restaurant subcollections - Settlement batch history
  batches: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.BATCHES),

  // Order operations and idempotency (three-axis state)
  orderOperations: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.ORDER_OPERATIONS),

  orderIdempotency: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.ORDER_IDEMPOTENCY),

  kitchenOperations: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.KITCHEN_OPERATIONS),

  kitchenIdempotency: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.KITCHEN_IDEMPOTENCY),

  // Payment Configuration - Can be at tenant or restaurant level
  // Tenant-level payment methods (applies to all restaurants)
  paymentMethods: (tenantDocId: string, locationDocId?: string): string =>
    locationDocId
      ? locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAYMENT_METHODS)
      : `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.PAYMENT_METHODS}`,

  // Live offline-capture queue status reported by the POS. One doc
  // per restaurant (`current`); see `OfflineQueueReporterService`
  // on the POS side and `useOfflineQueueStatus` on the client side.
  offlineQueueStatus: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.OFFLINE_QUEUE_STATUS),

  // `_meta/printer_config` — the restaurant's monotonic printer-config version.
  printerConfigVersion: (tenantDocId: string, locationDocId: string): string =>
    locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.RESTAURANT_META, "printer_config"),

  // Restaurant subcollections - Table Service
  tableLayouts: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TABLE_LAYOUTS),

  tableAssignments: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TABLE_ASSIGNMENTS),

  tableMerges: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TABLE_MERGES),

  // Restaurant subcollections - Customer Management
  waitlistEntries: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.WAITLIST_ENTRIES),

  seatedCustomers: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SEATED_CUSTOMERS),

  reservations: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.RESERVATIONS),

  // Restaurant subcollections - Device Management
  // Devices are at restaurant level for tenant/restaurant-scoped security
  devices: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.DEVICES),

  // Fleet management: per-device authoritative command subcollection (SSOT for
  // the self-managed Device-Owner control plane). commandId == doc id.
  deviceCommands: (tenantDocId: string, locationDocId: string, deviceDocId: string): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.DEVICES, deviceDocId)}/${COLLECTIONS.DEVICE_COMMANDS}`,

  deviceConfig: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.DEVICE_CONFIG),

  // Unified notifications (SSOT)
  notifications: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.NOTIFICATIONS),

  // New-Order Awareness ack + escalation state (doc id = orderGuid)
  orderAwareness: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.ORDER_AWARENESS),

  // Restaurant subcollections - OrderUp Books (Accounting)
  // ALL Books path helpers were removed across Phases 1-7 when the domain
  // migrated to Supabase. Browser code never reaches Books tables directly;
  // it goes through the Fastify API at /api/v1/books/*.
  // See docs/books-rebuild/BOOKS_REBUILD_PLAN.md Phases 1-7.

  // Restaurant subcollections - Newsletter
  newsletterSubscribers: (
    tenantDocId: string,
    locationDocId: string,
  ): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.NEWSLETTER_SUBSCRIBERS),

  // Restaurant subcollections - Contact Submissions
  contactSubmissions: (
    tenantDocId: string,
    locationDocId: string,
  ): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CONTACT_SUBMISSIONS),

  // Restaurant subcollections - Contact Subjects
  contactSubjects: (
    tenantDocId: string,
    locationDocId: string,
  ): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CONTACT_SUBJECTS),

  // Restaurant subcollections - Print Records
  printRecords: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PRINT_RECORDS),
  supportAccessLog: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SUPPORT_ACCESS_LOG),

  // Restaurant subcollections - Managed-router NetworkConfig versions
  networkConfigs: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.NETWORK_CONFIGS),

  // Restaurant subcollections - Managed-router secret values (server-side only)
  networkSecrets: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.NETWORK_SECRETS),

  // Restaurant subcollections - Transfer Records
  orderTransferRecords: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.ORDER_TRANSFER_RECORDS),
  // Restaurant subcollections - Checks (5-axis architecture)
  checks: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CHECKS),

  checkOperations: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CHECK_OPERATIONS),

  checkIdempotency: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CHECK_IDEMPOTENCY),

  // Restaurant subcollections - Payroll
  payrollSettings: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAYROLL_SETTINGS),

  payPeriods: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAY_PERIODS),

  payrollRuns: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAYROLL_RUNS),

  payStubs: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAY_STUBS),

  holidaySchedules: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.HOLIDAY_SCHEDULES),

  payrollAuditLogs: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAYROLL_AUDIT_LOGS),

  w2Forms: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.W2_FORMS),

  w2GenerationRuns: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.W2_GENERATION_RUNS),

  payrollDisbursements: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PAYROLL_DISBURSEMENTS),

  employerTaxForms: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.EMPLOYER_TAX_FORMS),

  nec1099Forms: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.NEC_1099_FORMS),

  newHireReports: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.NEW_HIRE_REPORTS),

  tipDeclarations: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.TIP_DECLARATIONS),
  cashTips: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.CASH_TIPS),

  // Platform-level collection - Tax Tables (shared across all tenants)
  taxTables: (): string => COLLECTIONS.TAX_TABLES,

  // Tenant subcollection - courier provider accounts (one per provider)
  courierAccounts: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.COURIER_ACCOUNTS}`,

  // Tenant subcollections - Gift Cards
  giftCards: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARDS}`,

  giftCardTransactions: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARD_TRANSACTIONS}`,

  giftCardPrograms: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARD_PROGRAMS}`,

  giftCardAlerts: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARD_ALERTS}`,

  giftCardProbeStats: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARD_PROBE_STATS}`,

  /** Top-level, cross-tenant: one guesser is one guesser on every tenant. */
  giftCardProbeGuards: (): string => COLLECTIONS.GIFT_CARD_PROBE_GUARDS,
  giftCardKeyPins: (): string => COLLECTIONS.GIFT_CARD_KEY_PINS,

  giftCardHolds: (tenantDocId: string, cardDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARDS}/${cardDocId}/${COLLECTIONS.GIFT_CARD_HOLDS}`,

  giftCardOnlineTenders: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARD_ONLINE_TENDERS}`,

  giftCardImports: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARD_IMPORTS}`,

  giftCardImportRows: (tenantDocId: string, importId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARD_IMPORTS}/${importId}/${COLLECTIONS.GIFT_CARD_IMPORT_ROWS}`,

  giftCardNumberReveals: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARD_NUMBER_REVEALS}`,

  giftCardPinEvents: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARD_PIN_EVENTS}`,

  giftCardNumberBackfill: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.GIFT_CARD_NUMBER_BACKFILL}`,

  egiftOrders: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.EGIFT_ORDERS}`,

  egiftPurchaseCounters: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.EGIFT_PURCHASE_COUNTERS}`,

  egiftDeliveryMessages: (): string => COLLECTIONS.EGIFT_DELIVERY_MESSAGES,

  tenantCounters: (tenantDocId: string): string =>
    `${COLLECTIONS.TENANTS}/${tenantDocId}/${COLLECTIONS.COUNTERS}`,

  // Platform config — top-level singleton documents (e.g., platform_config/mdm)
  platformMdmConfig: (): string =>
    `${COLLECTIONS.PLATFORM_CONFIG}/mdm`,

  // The delivery missed-order sweep's cursor + rate gate, one per provider
  // (platform_config/missed_order_sweep_kitchenhub). Admin-SDK-only.
  platformMissedOrderSweep: (provider: string): string =>
    `${COLLECTIONS.PLATFORM_CONFIG}/missed_order_sweep_${provider}`,

  // In-progress AMAPI enterprise signup (survives the Google redirect round-trip)
  platformMdmSignup: (): string =>
    `${COLLECTIONS.PLATFORM_CONFIG}/mdm_signup`,

  // Platform-level MDM policies — subcollection of the platform_config/mdm
  // singleton (3 segments → valid collection; the 2-segment form
  // platform_config/mdm_policies is a DOCUMENT and Firestore rejects it).
  mdmPolicies: (): string =>
    `${COLLECTIONS.PLATFORM_CONFIG}/mdm/${COLLECTIONS.MDM_POLICIES}`,

  // Platform-level MDM command audit log — subcollection of platform_config/mdm
  mdmCommands: (): string =>
    `${COLLECTIONS.PLATFORM_CONFIG}/mdm/${COLLECTIONS.MDM_COMMANDS}`,

  // Pending MDM enrollments (keyed by encoded hardware serial) — written by the
  // AMAPI enrollment webhook when the device hasn't registered yet; consumed
  // and deleted by deviceService.registerDevice once the device checks in.
  mdmPendingEnrollments: (): string =>
    `${COLLECTIONS.PLATFORM_CONFIG}/mdm/${COLLECTIONS.MDM_PENDING_ENROLLMENTS}`,

  // Platform-level enrollment tokens (QR codes). Enrollment is restaurant-
  // agnostic and site-admin-owned, so tokens live under platform_config/mdm —
  // NOT under any tenant/restaurant.
  mdmEnrollmentTokensPlatform: (): string =>
    `${COLLECTIONS.PLATFORM_CONFIG}/mdm/${COLLECTIONS.MDM_ENROLLMENT_TOKENS}`,

  // Fleet management derived read-model — a Cloud Function mirrors each device
  // command's state here for cross-tenant console queries. Admin-SDK-only. Uses
  // the 3-segment platform_config/fleet/fleet_commands form (the 2-segment
  // platform_config/fleet_commands is a DOCUMENT and Firestore rejects it),
  // mirroring mdmCommands().
  fleetCommands: (): string =>
    `${COLLECTIONS.PLATFORM_CONFIG}/fleet/${COLLECTIONS.FLEET_COMMANDS}`,

  // Remote-support relay sessions (P5b) — platform-level, cross-tenant,
  // Admin-SDK-only. Uses the 3-segment platform_config/fleet/support_sessions
  // form (the 2-segment platform_config/support_sessions is a DOCUMENT and
  // Firestore rejects it), mirroring fleetCommands(). Distinct from the LiveKit
  // POS support sessions at the top-level supportSessions() collection.
  fleetSupportSessions: (): string =>
    `${COLLECTIONS.PLATFORM_CONFIG}/fleet/${COLLECTIONS.SUPPORT_SESSIONS}`,

  // VPN access grants (managed-router §8). Admin-SDK-only, platform-level — an
  // operator's reach is an OrderUp-operations fact, not a tenant's data. Same
  // 3-segment platform_config/fleet/… form as fleetSupportSessions().
  fleetVpnGrants: (): string =>
    `${COLLECTIONS.PLATFORM_CONFIG}/fleet/${COLLECTIONS.VPN_GRANTS}`,

  // Deterministic Firestore doc ID for a pending enrollment, derived from the
  // hardware serial. MUST be used by both the webhook (writer) and
  // registerDevice (reader) so they resolve the same document. Firestore doc
  // IDs can't contain '/', so non-alphanumeric chars are mapped to '_'.
  mdmPendingEnrollmentId: (serialNumber: string): string =>
    serialNumber.replace(/[^A-Za-z0-9_-]/g, '_'),

  // Restaurant subcollections - Weather Forecasts
  weatherForecasts: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.WEATHER_FORECASTS),

  // Restaurant subcollections - Instagram Feed (cached synced posts)
  instagramFeed: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.INSTAGRAM_FEED),

  // Restaurant subcollections - Support Tickets (AI-triaged user help requests)
  supportTickets: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SUPPORT_TICKETS),

  // Ticket subcollection - the server-assembled triage report.
  //
  // Takes the ticket's DOC id, not its guid. The guid is a FIELD on the ticket
  // (the client mints it separately from the document id), so it cannot address
  // a parent in a subcollection path — `SupportTicketQueryService.getTicket`
  // returns the `documentPath` that resolves one.
  supportTicketTriageReports: (
    tenantDocId: string,
    locationDocId: string,
    ticketDocId: string,
  ): string =>
    `${locationSubdocPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SUPPORT_TICKETS, ticketDocId)}/${COLLECTIONS.SUPPORT_TICKET_TRIAGE_REPORTS}`,

  // Top-level - phone upload links for POS support tickets (hash-keyed, server-only)
  supportTicketUploadLinks: (): string => COLLECTIONS.SUPPORT_TICKET_UPLOAD_LINKS,

  // Top-level - per-printer state for the `printer_health` email (server-only)
  printerHealthAlerts: (): string => COLLECTIONS.PRINTER_HEALTH_ALERTS,

  // Top-level - phone upload links for tax-exemption certificates (hash-keyed, server-only)
  taxExemptionUploadLinks: (): string => COLLECTIONS.TAX_EXEMPTION_UPLOAD_LINKS,

  // Top-level - phone upload links for a menu item's photo (hash-keyed, server-only)
  menuItemPhotoUploadLinks: (): string => COLLECTIONS.MENU_ITEM_PHOTO_UPLOAD_LINKS,

  // Top-level - one-time menu → checkout sign-in hand-off codes (hash-keyed, server-only)
  checkoutHandoffs: (): string => COLLECTIONS.CHECKOUT_HANDOFFS,

  // Restaurant subcollections - Appointments (appointment-style scheduling)
  bookableServices: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.BOOKABLE_SERVICES),

  providerBookingProfiles: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.PROVIDER_BOOKING_PROFILES),

  appointments: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.APPOINTMENTS),

  appointmentDays: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.APPOINTMENT_DAYS),

  // Restaurant subcollections - Anonymous item feedback
  feedbackItems: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.FEEDBACK_ITEMS),

  feedbackResponses: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.FEEDBACK_RESPONSES),

  feedbackCards: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.FEEDBACK_CARDS),

  guestCodes: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.GUEST_CODES),

  // Restaurant subcollections — guest service requests
  serviceRequests: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SERVICE_REQUESTS),

  serviceRequestCounters: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.SERVICE_REQUEST_COUNTERS),

  appointmentPayments: (tenantDocId: string, locationDocId: string): string =>
    locationCollectionPath(scopeOf(tenantDocId, locationDocId), COLLECTIONS.APPOINTMENT_PAYMENTS),

  // Customer membership program enrollment — subcollection of a customer doc,
  // keyed by the customer's Firebase auth uid: customers/{uid}/memberships
  customerMemberships: (customerUid: string): string =>
    `${COLLECTIONS.CUSTOMERS}/${customerUid}/${COLLECTIONS.MEMBERSHIPS}`,

  // Customer FCM push-token registry — subcollection of a customer doc,
  // keyed by the customer's Firebase auth uid: customers/{uid}/push_tokens.
  // Doc id = sha256(token) hex (raw FCM tokens can exceed Firestore doc-id
  // limits and contain ':'). Server-write-only via the API.
  customerPushTokens: (customerUid: string): string =>
    `${COLLECTIONS.CUSTOMERS}/${customerUid}/${COLLECTIONS.PUSH_TOKENS}`,
} as const;

/*
// Helper functions for document ID resolution
// These are utilities that services should use to resolve business IDs to document IDs
export const DocumentResolution = {
  // NOTE: These are helper utilities - actual implementation should be in services
  // with proper Firestore query logic to resolve business IDs to document IDs


   * Placeholder for resolving tenant business ID to Firestore document ID
   * Implementation should query: tenants collection where tenantId == businessId

  resolveTenantDocId: async (tenantId: number): Promise<string | null> => {
    throw new Error('DocumentResolution.resolveTenantDocId must be implemented by services');
  },


   * Placeholder for resolving restaurant business ID to Firestore document ID
   * Implementation should query: tenantLocationsPath(tenantDocId) where restaurantId == businessId

  resolveRestaurantDocId: async (tenantDocId: string, restaurantId: number): Promise<string | null> => {
    throw new Error('DocumentResolution.resolveRestaurantDocId must be implemented by services');
  },
};
*/

// Utility functions for path analysis
export const PathUtils = {
  // Check if a path is for a restaurant subcollection
  isRestaurantSubcollection: (path: string): boolean => {
    return (
      path.includes(`/${locationsCollectionId()}/`) &&
      path.split("/").length >= 4
    );
  },

  // Extract tenant document ID from a path (not business ID!)
  extractTenantDocId: (path: string): string | null => {
    const parts = path.split("/");
    const tenantIndex = parts.indexOf(COLLECTIONS.TENANTS);
    return tenantIndex >= 0 && parts[tenantIndex + 1]
      ? parts[tenantIndex + 1]
      : null;
  },

  // Extract restaurant document ID from a path (not business ID!)
  extractRestaurantDocId: (path: string): string | null => {
    const parts = path.split("/");
    const locationIndex = parts.indexOf(locationsCollectionId());
    return locationIndex >= 0 && parts[locationIndex + 1]
      ? parts[locationIndex + 1]
      : null;
  },

  // Build a complete document path
  buildDocumentPath: (collectionPath: string, documentId: string): string => {
    return `${collectionPath}/${documentId}`;
  },
};

// Export for convenience
export { COLLECTIONS as Collections };
